import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'

import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Paper,
    Grid,
    TextField,
    makeStyles,
    Theme,
    createStyles,
} from '@material-ui/core'
import {
    FirstPage,
    LastPage,
    NavigateBefore,
    NavigateNext,
    Close,
} from '@material-ui/icons'

import { CompanyWithInvoiceCode } from '../types'
import { UpdatePropertyRequest } from '../../hooks'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        navigationContainer: {
            background: 'linear-gradient(135deg, #00a8a0 0%, #008C85 100%)',
            borderRadius: theme.spacing(3),
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            color: theme.palette.primary.contrastText,
            boxShadow: theme.shadows[8],
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                    'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                pointerEvents: 'none',
            },
        },
        navigationContent: {
            position: 'relative',
            zIndex: 1,
        },
        navigationInfo: {
            fontSize: '1rem',
            fontWeight: 500,
            textShadow: `0 2px 8px ${theme.palette.common.black}30`,
            marginBottom: theme.spacing(0.5),
        },
        navigationSubtitle: {
            fontSize: '1rem',
            opacity: 0.9,
            fontWeight: 500,
            textShadow: `0 1px 4px ${theme.palette.common.black}20`,
        },
        navigationButton: {
            backgroundColor: `${theme.palette.common.white}20`,
            color: theme.palette.primary.contrastText,
            border: `2px solid ${theme.palette.common.white}40`,
            borderRadius: theme.spacing(2),
            margin: theme.spacing(0, 0.75),
            padding: theme.spacing(1, 2),
            minWidth: 100,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.9rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
                backgroundColor: `${theme.palette.common.white}35`,
                border: `2px solid ${theme.palette.common.white}60`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${theme.palette.common.black}25`,
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: `0 4px 15px ${theme.palette.common.black}20`,
            },
            '&:disabled': {
                backgroundColor: `${theme.palette.common.white}08`,
                color: `${theme.palette.common.white}40`,
                border: `2px solid ${theme.palette.common.white}15`,
                transform: 'none',
                boxShadow: 'none',
            },
        },
        buttonGroup: {
            display: 'flex',
            gap: theme.spacing(1),
            alignItems: 'center',
        },
        propertyCard: {
            borderRadius: theme.spacing(2),
            boxShadow: theme.shadows[2],
        },
        propertyTitle: {
            color: theme.palette.primary.main,
            fontWeight: 600,
            marginBottom: theme.spacing(2),
            paddingBottom: theme.spacing(1),
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        formField: {
            marginBottom: theme.spacing(2),
        },
        infoBox: {
            backgroundColor: theme.palette.grey[100],
            borderRadius: theme.spacing(1),
            padding: theme.spacing(2),
        },
        saveButton: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            marginTop: theme.spacing(3),
            padding: theme.spacing(1.5, 4),
            borderRadius: theme.spacing(2),
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: theme.shadows[2],
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
                boxShadow: theme.shadows[4],
                transform: 'translateY(-1px)',
            },
            '&:active': {
                transform: 'translateY(0)',
            },
        },
        closeButton: {
            position: 'absolute',
            top: theme.spacing(2),
            right: theme.spacing(2),
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: theme.palette.primary.contrastText,
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '50%',
            minWidth: 40,
            width: 40,
            height: 40,
            zIndex: 10,
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.5)',
            },
        },
    }),
)

interface Props {
    propertyList: CompanyWithInvoiceCode[]
    onClose: () => void
    updateProperty: (
        req: UpdatePropertyRequest,
    ) => Promise<CompanyWithInvoiceCode>
}

export const BulkEditPropertiesBilling = (props: Props) => {
    const { propertyList, onClose, updateProperty } = props
    const [currentIndex, setCurrentIndex] = useState(0)
    const [billableUnits, setBillableUnits] = useState<{
        [key: number]: string
    }>({})
    const [billableUnitRate, setBillableUnitRate] = useState<{
        [key: number]: string
    }>({})

    const classes = useStyles()

    // Initialize state when propertyList changes
    useEffect(() => {
        if (propertyList.length > 0) {
            const initialBillableUnits: { [key: number]: string } = {}
            const initialBillableUnitRate: { [key: number]: string } = {}

            propertyList.forEach((property) => {
                initialBillableUnits[
                    property.id
                ] = property.billable_unit_count.toString()
                initialBillableUnitRate[
                    property.id
                ] = property.billable_unit_rate.toString()
            })

            setBillableUnits(initialBillableUnits)
            setBillableUnitRate(initialBillableUnitRate)
        }
    }, [propertyList])

    const goToNext = () => {
        if (currentIndex < propertyList.length - 1) {
            setCurrentIndex(currentIndex + 1)
        }
    }

    const goToPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1)
        }
    }

    const goToFirst = () => {
        setCurrentIndex(0)
    }

    const goToLast = () => {
        setCurrentIndex(propertyList.length - 1)
    }

    const handleBillableUnitsChange = (value: string) => {
        setBillableUnits((prev) => ({
            ...prev,
            [currentProperty.id]: value,
        }))
    }

    const handleBillableUnitRateChange = (value: string) => {
        setBillableUnitRate((prev) => ({
            ...prev,
            [currentProperty.id]: value,
        }))
    }

    if (propertyList.length === 0) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: 200 }}
            >
                <Typography variant="h6" color="textSecondary">
                    No properties to edit
                </Typography>
            </Box>
        )
    }

    const currentProperty = propertyList[currentIndex]
    const isFirst = currentIndex === 0
    const isLast = currentIndex === propertyList.length - 1

    const handleSave = () => {
        const req: UpdatePropertyRequest = {
            params: {
                id: currentProperty.id,
            },
            body: {
                billable_unit_count: Number(billableUnits[currentProperty.id]),
                billable_unit_rate: Number(
                    billableUnitRate[currentProperty.id],
                ),
            },
        }

        updateProperty(req)
            .then(() => {
                toast.success(
                    'Billing info updated successfully for ' +
                        currentProperty.name,
                )
            })
            .catch((error) => {
                toast.error(
                    'Failed to update billing info for ' + currentProperty.name,
                )
            })
    }

    return (
        <Box maxWidth={800} minWidth={800} mx="auto" p={3}>
            {/* Enhanced Navigation Header */}
            <Paper className={classes.navigationContainer} elevation={0}>
                {onClose && (
                    <Button
                        className={classes.closeButton}
                        onClick={onClose}
                        size="small"
                    >
                        <Close />
                    </Button>
                )}
                <div className={classes.navigationContent}>
                    <Grid container alignItems="center" justify="space-between">
                        <Grid item>
                            <Typography className={classes.navigationInfo}>
                                Property {currentIndex + 1} of{' '}
                                {propertyList.length}
                            </Typography>
                            <Typography className={classes.navigationSubtitle}>
                                {currentProperty.name}
                            </Typography>
                        </Grid>
                        <Grid item style={{ marginRight: 60 }}>
                            <div className={classes.buttonGroup}>
                                <Button
                                    className={classes.navigationButton}
                                    startIcon={<FirstPage />}
                                    onClick={goToFirst}
                                    disabled={isFirst}
                                    size="small"
                                >
                                    First
                                </Button>
                                <Button
                                    className={classes.navigationButton}
                                    startIcon={<NavigateBefore />}
                                    onClick={goToPrevious}
                                    disabled={isFirst}
                                    size="small"
                                >
                                    Previous
                                </Button>
                                <Button
                                    className={classes.navigationButton}
                                    endIcon={<NavigateNext />}
                                    onClick={goToNext}
                                    disabled={isLast}
                                    size="small"
                                >
                                    Next
                                </Button>
                                <Button
                                    className={classes.navigationButton}
                                    endIcon={<LastPage />}
                                    onClick={goToLast}
                                    disabled={isLast}
                                    size="small"
                                >
                                    Last
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </Paper>

            {/* Property Details with Editable Fields */}
            <Card className={classes.propertyCard} elevation={2}>
                <CardContent>
                    <Typography variant="h6" className={classes.propertyTitle}>
                        {currentProperty.name}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Billable Units"
                                variant="outlined"
                                type="number"
                                value={billableUnits[currentProperty.id] || ''}
                                onChange={(e) =>
                                    handleBillableUnitsChange(e.target.value)
                                }
                                className={classes.formField}
                                helperText="Number of billable units"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Billable Unit Rate"
                                variant="outlined"
                                type="number"
                                value={
                                    billableUnitRate[currentProperty.id] || ''
                                }
                                onChange={(e) =>
                                    handleBillableUnitRateChange(e.target.value)
                                }
                                className={classes.formField}
                                helperText="Rate per unit"
                            />
                        </Grid>
                    </Grid>

                    <Button className={classes.saveButton} onClick={handleSave}>
                        Save Changes
                    </Button>
                </CardContent>
            </Card>
        </Box>
    )
}
